import { Injectable } from '@angular/core';

export interface JwtClaims {
  [key: string]: any;
  iss?: string; // Issuer
  sub?: string; // Subject (user ID)
  aud?: string | string[]; // Audience
  exp?: number; // Expiration time
  nbf?: number; // Not before
  iat?: number; // Issued at
  jti?: string; // JWT ID
  tid?: string; // Tenant ID (Azure AD specific)
  oid?: string; // Object ID (Azure AD specific)
  upn?: string; // User Principal Name (Azure AD specific)
  preferred_username?: string; // Preferred username (Azure AD specific)
  name?: string; // Display name
  given_name?: string; // First name
  family_name?: string; // Last name
  email?: string; // Email address
  roles?: string[]; // User roles
  groups?: string[]; // User groups
}

@Injectable({
  providedIn: 'root'
})
export class JwtUtilsService {

  /**
   * Decode JWT token and return the claims
   * @param token - The JWT token to decode
   * @returns The decoded claims or null if invalid
   */
  decodeToken(token: string): JwtClaims | null {
    try {
      // JWT tokens have three parts separated by dots: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT token format');
        return null;
      }

      // Decode the payload (second part)
      const payload = parts[1];
      
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      
      // Decode base64
      const decodedPayload = atob(paddedPayload);
      
      // Parse JSON
      const claims = JSON.parse(decodedPayload);
      
      return claims;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Extract customer tenant ID from JWT token
   * @param token - The JWT token
   * @returns The customer tenant ID or empty string if not found
   */
  getCustomerTenantId(token: string): string {
    const claims = this.decodeToken(token);
    return claims?.tid || '';
  }

  /**
   * Extract user object ID from JWT token
   * @param token - The JWT token
   * @returns The object ID or empty string if not found
   */
  getObjectId(token: string): string {
    const claims = this.decodeToken(token);
    return claims?.oid || claims?.sub || '';
  }

  /**
   * Extract user principal name from JWT token
   * @param token - The JWT token
   * @returns The UPN or empty string if not found
   */
  getUserPrincipalName(token: string): string {
    const claims = this.decodeToken(token);
    return claims?.upn || claims?.preferred_username || '';
  }

  /**
   * Extract user display name from JWT token
   * @param token - The JWT token
   * @returns The display name or empty string if not found
   */
  getDisplayName(token: string): string {
    const claims = this.decodeToken(token);
    return claims?.name || '';
  }

  /**
   * Extract user email from JWT token
   * @param token - The JWT token
   * @returns The email or empty string if not found
   */
  getEmail(token: string): string {
    const claims = this.decodeToken(token);
    return claims?.email || claims?.upn || claims?.preferred_username || '';
  }

  /**
   * Extract user roles from JWT token
   * @param token - The JWT token
   * @returns Array of roles or empty array if not found
   */
  getRoles(token: string): string[] {
    const claims = this.decodeToken(token);
    return claims?.roles || [];
  }

  /**
   * Extract user groups from JWT token
   * @param token - The JWT token
   * @returns Array of groups or empty array if not found
   */
  getGroups(token: string): string[] {
    const claims = this.decodeToken(token);
    return claims?.groups || [];
  }

  /**
   * Check if token is expired
   * @param token - The JWT token
   * @returns True if token is expired, false otherwise
   */
  isTokenExpired(token: string): boolean {
    const claims = this.decodeToken(token);
    if (!claims || !claims.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return claims.exp < currentTime;
  }

  /**
   * Get token expiration time
   * @param token - The JWT token
   * @returns Expiration time as Date object or null if not found
   */
  getTokenExpiration(token: string): Date | null {
    const claims = this.decodeToken(token);
    if (!claims || !claims.exp) {
      return null;
    }

    return new Date(claims.exp * 1000);
  }

  /**
   * Get all claims from JWT token
   * @param token - The JWT token
   * @returns All claims as an object
   */
  getAllClaims(token: string): JwtClaims | null {
    return this.decodeToken(token);
  }

  /**
   * Check if user has specific role
   * @param token - The JWT token
   * @param role - The role to check for
   * @returns True if user has the role, false otherwise
   */
  hasRole(token: string, role: string): boolean {
    const roles = this.getRoles(token);
    return roles.includes(role);
  }

  /**
   * Check if user is in specific group
   * @param token - The JWT token
   * @param group - The group to check for
   * @returns True if user is in the group, false otherwise
   */
  isInGroup(token: string, group: string): boolean {
    const groups = this.getGroups(token);
    return groups.includes(group);
  }
}
